/* EYEKONIK round 5d: scoped menu enhancements with drawer logo and search. No persistent state or dependencies. */
(() => {
  'use strict';

  const ownMenu = '.navigation__tier-1[data-eyk-mob-own="true"]';
  const promo = '.navigation__column--promotion';
  const desktopContainer = 'main-navigation .navigation__tier-1-container';
  let observers = [];
  let drawerWasOpen = false;
  let openPanels = new Set();
  let nextId = 0;

  // Each feature, element and asynchronous callback fails independently.
  const safe = (fn) => {
    try { return fn(); } catch (error) { return undefined; }
  };
  const each = (selector, root, fn) => {
    root.querySelectorAll(selector).forEach((element) => safe(() => fn(element)));
  };
  const emit = (event, fields = {}) => {
    const detail = { event, ...fields };
    safe(() => {
      if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        window.dataLayer.push({ ...detail });
      }
    });
    safe(() => document.dispatchEvent(new CustomEvent('eyk:nav', { detail: { ...detail } })));
  };

  const stripImages = (root) => {
    each(`${promo} img[data-manual-src]`, root, (img) => {
      if (img.dataset.eykImageHandled) return;
      img.dataset.eykSrc = img.getAttribute('data-manual-src');
      if (img.hasAttribute('data-manual-srcset')) {
        img.dataset.eykSrcset = img.getAttribute('data-manual-srcset');
        img.removeAttribute('data-manual-srcset');
      }
      img.removeAttribute('data-manual-src');
      img.dataset.eykImageHandled = 'true';
    });
  };
  const restoreImages = (root) => {
    each(`${promo} img[data-eyk-src]`, root, (img) => {
      // Match Symmetry's manual-loader contract, assigning srcset before src.
      img.setAttribute('data-manual-src', img.dataset.eykSrc);
      if (img.hasAttribute('data-eyk-srcset')) {
        img.setAttribute('data-manual-srcset', img.dataset.eykSrcset);
        img.setAttribute('srcset', img.dataset.eykSrcset);
        img.removeAttribute('data-manual-srcset');
        img.removeAttribute('data-eyk-srcset');
      }
      img.setAttribute('src', img.dataset.eykSrc);
      img.removeAttribute('data-manual-src');
      img.removeAttribute('data-eyk-src');
    });
  };
  const prepareImages = () => {
    each('main-navigation, .mobile-navigation-drawer', document, stripImages);
    each(desktopContainer, document, (container) => {
      if (!container.querySelector(`${promo} img[data-eyk-src]`)
          || container.dataset.eykPreloadBound) return;
      const names = ['mouseenter', 'focusin', 'keydown'];
      // At wide viewports the visible root tabs are Symmetry's proxy nav, which lives OUTSIDE
      // main-navigation (main.js:2020) and carries the hover that opens this container's panel.
      // Binding only the container would leave the tiles unloaded until the pointer reaches the
      // open panel, i.e. blank tiles on the first open. Measured 7 Sep: at 1648px the proxy links
      // have real rects and the main-navigation ones are 0x0.
      const hosts = [container];
      each('main-navigation[data-proxy-nav]', document, (nav) => {
        const proxy = document.getElementById(nav.dataset.proxyNav);
        if (proxy && !hosts.includes(proxy)) hosts.push(proxy);
      });
      const load = (event) => safe(() => {
        const host = (event && event.currentTarget) || container;
        if (!window.matchMedia('(min-width: 768px)').matches
            || !host.getClientRects().length) return;
        restoreImages(container);
        hosts.forEach((element) => names.forEach((name) => element.removeEventListener(name, load)));
      });
      hosts.forEach((element) => names.forEach((name) => element.addEventListener(name, load)));
      container.dataset.eykPreloadBound = 'true';
    });
  };

  const menuRoots = () => {
    const roots = [...document.querySelectorAll('main-navigation, .mobile-navigation-drawer')];
    // Inline desktop links live in Symmetry's proxy, outside main-navigation.
    each('main-navigation[data-proxy-nav]', document, (nav) => {
      const proxy = document.getElementById(nav.dataset.proxyNav);
      if (proxy) roots.push(proxy);
    });
    return roots;
  };
  const makeLogo = () => {
    const source = document.querySelector('.logo .logo__link') || document.querySelector('.logo__link');
    if (!source) return null;
    const sourceImage = source.querySelector('img');
    const link = document.createElement('a');
    link.className = 'eyk-logo';
    link.setAttribute('href', source.getAttribute('href') || '/');
    link.setAttribute('aria-label', source.getAttribute('title')
      || (sourceImage && sourceImage.getAttribute('alt')) || 'EYEKØNIK');
    if (sourceImage) {
      const img = document.createElement('img');
      if (sourceImage.hasAttribute('srcset')) img.setAttribute('srcset', sourceImage.getAttribute('srcset'));
      img.setAttribute('src', sourceImage.getAttribute('data-manual-src') || sourceImage.currentSrc || sourceImage.src);
      img.setAttribute('alt', sourceImage.getAttribute('alt') || '');
      ['width', 'height'].forEach((name) => {
        if (sourceImage.hasAttribute(name)) img.setAttribute(name, sourceImage.getAttribute(name));
      });
      img.setAttribute('decoding', 'async');
      link.appendChild(img);
    } else {
      const text = document.createElement('span');
      text.className = 'eyk-logo__text';
      text.textContent = source.textContent.trim();
      link.appendChild(text);
    }
    link.dataset.eykHandled = 'true';
    return link;
  };
  const makeSearch = () => {
    const source = document.querySelector('.main-search__form');
    const sourceInput = source && source.querySelector('input[name="q"]');
    const form = document.createElement('form');
    form.className = 'eyk-search';
    form.setAttribute('role', 'search');
    form.method = 'get';
    form.setAttribute('action', source ? source.getAttribute('action') || '/search' : '/search');
    if (source) {
      each('input[type="hidden"]', source, (input) => {
        const clone = input.cloneNode(true);
        clone.removeAttribute('id');
        form.appendChild(clone);
      });
    }
    // Real submit button with a magnifier (as Caintè's Symmetry drawer search), then a real input with a real placeholder.
    const button = document.createElement('button');
    button.type = 'submit';
    button.className = 'eyk-search__btn';
    button.setAttribute('aria-label', document.documentElement.dataset.eykSearch || 'Search');
    button.innerHTML = '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false"><circle cx="6.8" cy="6.8" r="5" fill="none" stroke="currentColor" stroke-width="1.5"></circle><line x1="10.6" y1="10.6" x2="14.5" y2="14.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"></line></svg>';
    const input = document.createElement('input');
    input.type = 'text';
    input.name = 'q';
    input.placeholder = (document.documentElement.dataset.eykSearch || 'Search') + '\u2026';
    input.setAttribute('aria-label', sourceInput ? sourceInput.getAttribute('aria-label') || document.documentElement.dataset.eykSearch || 'Search' : document.documentElement.dataset.eykSearch || 'Search');
    input.setAttribute('enterkeyhint', 'search');
    input.setAttribute('autocomplete', 'off');
    form.appendChild(input);
    form.appendChild(button); // magnifier on the right, as Caintè's search bar (Michiel, 9 sep 01:57)
    // The pill launches the theme's search panel (bar + our five tiles), like the header magnifier; the drawer closes first.
    // Without the header search link (setting "Enable search" off) it stays a plain search form.
    const openPanel = () => {
      const trigger = document.querySelector('.show-search-link');
      if (!trigger) return false;
      const drawer = form.closest('.mobile-navigation-drawer');
      const closeBtn = drawer && drawer.querySelector('.mobile-nav-toggle');
      if (closeBtn) safe(() => closeBtn.click());
      // Synchronous, inside the tap gesture: iOS only opens the keyboard for a focus() that happens in the gesture itself.
      window.__eykSyntheticSearchOpen = true;
      safe(() => trigger.click());
      window.__eykSyntheticSearchOpen = false;
      const field = document.querySelector('.main-search__input');
      if (field) {
        if (input.value.trim()) { field.value = input.value; field.dispatchEvent(new Event('input', { bubbles: true })); }
        safe(() => field.focus({ preventScroll: true }));
        // Stock timing: the search opener sets theme.trapOpener = the header link at 500ms; the drawer close calls
        // removeTrapFocus(theme.trapOpener) at 750ms, which would move focus to that link (Codex #2 P1, measured). Point the
        // opener at the field before 750ms and guard focus for the transition window.
        // Cancellable handoff (Codex #3 P1): every step checks the panel is still open; the whole thing stops when the panel
        // closes, and after the 750ms drawer cleanup the return-focus target goes back to the header link and the panel's own
        // focus trap is re-installed (the cleanup removed it).
        const panel = field.closest('.main-search');
        const alive = () => document.body.classList.contains('show-search') && field.isConnected;
        const timers = [];
        const at = (ms, fn) => timers.push(window.setTimeout(() => safe(() => { if (alive()) fn(); }), ms));
        const refocus = () => { if (document.activeElement !== field) field.focus({ preventScroll: true }); };
        at(350, () => { if (window.theme) window.theme.trapOpener = field; refocus(); });
        at(560, () => { if (window.theme) window.theme.trapOpener = field; refocus(); });
        at(800, () => {
          if (window.theme) window.theme.trapOpener = trigger;
          if (typeof window.trapFocus === 'function' && panel) window.trapFocus(panel, field); else refocus();
        });
        at(1100, () => { const a = document.activeElement; if (!(a && panel && panel.contains(a))) refocus(); });
        const guard = (ev) => safe(() => { if (alive() && ev.target !== field && ev.target.closest && ev.target.closest('.show-search-link, .mobile-nav-toggle')) field.focus({ preventScroll: true }); });
        document.addEventListener('focusin', guard, true);
        const stop = () => safe(() => { timers.forEach(window.clearTimeout); document.removeEventListener('focusin', guard, true); observer.disconnect(); });
        const observer = new MutationObserver(() => safe(() => { if (!document.body.classList.contains('show-search')) stop(); }));
        observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
        window.setTimeout(stop, 1500);
      }
      emit('nav_search_open', { surface: 'mobile', from: 'drawer' });
      return true;
    };
    form.addEventListener('click', (event) => safe(() => {
      // A populated field submits natively via the magnifier (keyboard users); otherwise the pill launches the panel.
      if (input.value.trim() && event.target.closest('.eyk-search__btn')) return;
      if (openPanel()) { event.preventDefault(); return; }
      if (event.target !== input && !input.value.trim()) { event.preventDefault(); input.focus(); }
    }));
    form.addEventListener('submit', (event) => safe(() => {
      if (!input.value.trim()) { event.preventDefault(); input.focus(); return; }
      emit('nav_search_submit', { surface: 'mobile' });
    }));
    form.dataset.eykHandled = 'true';
    return form;
  };

  // Search panel: the five SHOP ALL tiles under the search bar (mobile and desktop), cloned from the header promotion blocks.
  const buildSearchTiles = () => safe(() => {
    const panel = document.querySelector('main-search.main-search, .main-search');
    if (!panel || panel.querySelector('.eyk-search-tiles')) return;
    const container = panel.querySelector('.main-search__container') || panel;
    safe(() => {
      if (!panel.dataset.eykSearchEvents) {
        each('.show-search-link', document, (link) => link.addEventListener('click', () => safe(() => {
          if (!window.__eykSyntheticSearchOpen) emit('nav_search_open', { surface: window.innerWidth < 768 ? 'mobile' : 'desktop', from: 'header' });
        })));
        const form = panel.querySelector('.main-search__form');
        if (form) form.addEventListener('submit', () => safe(() => emit('nav_search_submit', { surface: 'panel' })));
        panel.dataset.eykSearchEvents = 'true';
      }
    });
    safe(() => {
      const field = panel.querySelector('.main-search__input');
      if (!field) return;
      field.placeholder = 'Search\u2026';
      // Stock keeps --no-results after the field is cleared, which would keep the tiles hidden (Codex #2 P2).
      field.addEventListener('input', () => safe(() => { if (!field.value.trim()) panel.classList.remove('main-search--no-results'); }));
    });
    const nav = document.querySelector('main-navigation');
    if (!nav) return;
    const shopAll = [...nav.querySelectorAll('.navigation__tier-1 > .navigation__item')]
      .find((li) => { const a = li.querySelector(':scope > .navigation__link'); return a && a.textContent.trim().toUpperCase() === 'SHOP ALL'; });
    if (!shopAll) return;
    const promos = [...shopAll.querySelectorAll('.menu-promotion')];
    if (!promos.length) return;
    const wrap = document.createElement('div');
    wrap.className = 'eyk-search-tiles';
    const label = document.createElement('div');
    label.className = 'eyk-search-tiles__label';
    label.textContent = 'Explore all our categories'; // Michiel 9 sep 12:50 (was 'Shop all'); CSS sets it uppercase. Analytics group stays 'Shop all' = the source tab of the five tiles.
    wrap.appendChild(label);
    const row = document.createElement('div');
    row.className = 'eyk-search-tiles__row';
    promos.forEach((promo) => {
      const link = promo.querySelector('a');
      const img = promo.querySelector('img');
      const text = promo.querySelector('.menu-promotion__text');
      if (!link || !img) return;
      const src = img.getAttribute('data-eyk-src') || img.currentSrc || img.getAttribute('src') || '';
      const srcset = img.getAttribute('data-eyk-srcset') || img.getAttribute('srcset') || '';
      if (!src) return;
      const a = document.createElement('a');
      a.className = 'eyk-search-tiles__tile';
      a.href = link.getAttribute('href');
      const pic = document.createElement('img');
      pic.src = src;
      if (srcset) pic.srcset = srcset;
      pic.sizes = '(min-width: 768px) 144px, 20vw';
      pic.alt = img.getAttribute('alt') || '';
      pic.width = 544; pic.height = 1088;
      pic.loading = 'lazy';
      pic.decoding = 'async';
      a.appendChild(pic);
      const cap = document.createElement('span');
      cap.className = 'eyk-search-tiles__caption';
      cap.textContent = text ? text.textContent.trim() : (img.getAttribute('alt') || '');
      a.appendChild(cap);
      a.addEventListener('click', () => safe(() => emit('nav_click', { surface: 'search', group: 'Shop all', label: cap.textContent, kind: 'tile', href: a.getAttribute('href') })));
      row.appendChild(a);
    });
    if (!row.children.length) return;
    wrap.appendChild(row);
    container.appendChild(wrap);
  });

  const refreshFocus = (drawer) => safe(() => {
    if (document.body.classList.contains('reveal-mobile-nav')
        && typeof removeTrapFocus === 'function' && typeof trapFocus === 'function') {
      removeTrapFocus();
      trapFocus(drawer, drawer.querySelector('.mobile-nav-toggle'));
    }
  });
  const prepareDrawer = (drawer) => {
    const ul = drawer.querySelector(ownMenu);
    if (!ul) return;
    drawer.classList.add('eyk-mob');
    safe(() => {
      if (drawer.dataset.eykDriftGuard) return;
      drawer.addEventListener('click', (event) => safe(() => {
        const toggle = event.target.closest('.navigation__children-toggle');
        if (toggle && toggle.matches('.navigation__tier-1 > .navigation__item > .navigation__children-toggle')
            && ul.contains(toggle)) {
          event.preventDefault();
          event.stopPropagation();
        }
      }), true);
      drawer.classList.remove('mobile-navigation-drawer--child-open');
      drawer.dataset.eykDriftGuard = 'true';
    });
    safe(() => {
      const bar = drawer.querySelector('.navigation__mobile-header');
      const close = bar && bar.querySelector('.mobile-nav-toggle');
      if (!bar || !close) return;
      safe(() => {
        if (!bar.querySelector('.eyk-logo')) {
          const logo = makeLogo();
          if (logo) bar.insertBefore(logo, bar.querySelector('.eyk-search') || close);
        }
      });
      safe(() => { if (!bar.querySelector('.eyk-search')) bar.insertBefore(makeSearch(), close); });
    });
    let groupIndex = 0;
    [...ul.children].forEach((item) => {
      if (!item.classList.contains('navigation__item--with-children')) return;
      const initiallyOpen = groupIndex++ === 0;
      safe(() => {
        if (item.dataset.eykGroupHandled) return;
        const label = item.querySelector(':scope > .navigation__link');
        const panel = item.querySelector(':scope > .navigation__tier-2-container');
        if (!label || !panel) return;
        label.removeAttribute('aria-haspopup');
        label.removeAttribute('aria-expanded');
        if (!panel.id) panel.id = `EykMenuGroup-${++nextId}`;
        label.setAttribute('role', 'button');
        label.setAttribute('aria-controls', panel.id);
        label.setAttribute('aria-expanded', String(initiallyOpen));
        item.classList.toggle('eyk-open', initiallyOpen);
        item.classList.remove('navigation__item--open');
        label.addEventListener('click', (event) => safe(() => {
          event.preventDefault();
          event.stopPropagation();
          const open = item.classList.toggle('eyk-open');
          label.setAttribute('aria-expanded', String(open));
        }));
        label.addEventListener('keydown', (event) => safe(() => {
          if (event.key === ' ') {
            event.preventDefault();
            label.click();
          }
        }));
        // The chevron is a ::after on the row, so a tap on it targets the row itself; route it to the label.
        item.addEventListener('click', (event) => safe(() => {
          if (event.target !== item && event.target.closest('a, button, input, select, label')) return;
          if (event.target !== item && !item.contains(event.target)) return;
          event.preventDefault();
          event.stopPropagation();
          label.click();
        }));
        each('svg', item, (icon) => icon.setAttribute('aria-hidden', 'true'));
        item.dataset.eykGroupHandled = 'true';
      });
    });
    if (!drawer.dataset.eykDrawerHandled) refreshFocus(drawer);
    drawer.dataset.eykDrawerHandled = 'true';
  };
  const observe = (element, options, fn) => {
    const observer = new MutationObserver(() => safe(fn));
    observer.observe(element, options);
    observers.push(observer);
  };
  const prepareScrim = () => {
    const header = document.querySelector('.section-header');
    if (!header) return;
    const sync = () => {
      document.body.classList.toggle('eyk-nav-open', header.classList.contains('section-header--nav-hover')
        || header.classList.contains('section-header--nav-open'));
    };
    document.body.classList.add('eyk-nav-ready');
    observe(header, { attributes: true, attributeFilter: ['class'] }, sync);
    sync();
  };
  const prepareEvents = () => {
    menuRoots().forEach((root) => safe(() => {
      if (root.dataset.eykClickEvents) return;
      root.addEventListener('click', (event) => safe(() => {
        const link = event.target.closest('a[href]');
        if (!link || event.defaultPrevented || !root.contains(link)
            || !link.matches('.navigation__link, .menu-promotion__link, .mobile-navigation-drawer__footer a')) return;
        const item = link.closest('.navigation__tier-1 > .navigation__item');
        const parent = item && item.querySelector(':scope > .navigation__link');
        emit('nav_click', {
          surface: link.closest('.mobile-navigation-drawer') ? 'mobile' : 'desktop',
          group: parent ? parent.textContent.trim() : 'Footer',
          label: link.textContent.trim(),
          kind: link.closest(promo) ? 'tile' : 'text',
          href: link.getAttribute('href')
        });
      }));
      root.dataset.eykClickEvents = 'true';
    }));
    const syncDrawer = () => {
      const drawer = document.querySelector('.mobile-navigation-drawer');
      const open = !!drawer && document.body.classList.contains('reveal-mobile-nav');
      if (open && !drawerWasOpen) emit('nav_open', { surface: 'mobile' });
      drawerWasOpen = open;
    };
    observe(document.body, { attributes: true, attributeFilter: ['class'] }, syncDrawer);
    syncDrawer();
    const syncDesktop = () => {
      const current = new Set();
      if (window.matchMedia('(min-width: 768px)').matches) {
        each('main-navigation .navigation__tier-1 > .navigation__item--with-children', document, (item) => {
          const panel = item.querySelector(':scope > .navigation__tier-2-container');
          const shown = item.classList.contains('navigation__item--show-children')
            || (document.body.classList.contains('tab-used') && item.matches(':focus-within'));
          if (!shown || !panel || !panel.getClientRects().length
              || getComputedStyle(panel).visibility !== 'visible') return;
          current.add(item);
          if (!openPanels.has(item)) {
            const label = item.querySelector(':scope > .navigation__link');
            emit('nav_open', { surface: 'desktop', group: label ? label.textContent.trim() : '' });
          }
        });
      }
      openPanels = current;
    };
    each('main-navigation .navigation__tier-1', document, (ul) => {
      observe(ul, { attributes: true, attributeFilter: ['class'], subtree: true }, syncDesktop);
      if (!ul.dataset.eykFocusEvents) {
        const syncAfterFocus = () => safe(() => window.requestAnimationFrame(() => safe(syncDesktop)));
        ul.addEventListener('focusin', syncAfterFocus);
        ul.addEventListener('focusout', syncAfterFocus);
        ul.dataset.eykFocusEvents = 'true';
      }
    });
    syncDesktop();
  };
  const prepareStockDrawerImages = () => {
    // Clearing Mobile menu restores the native drawer, including visible promos.
    const sync = () => {
      if (!document.body.classList.contains('reveal-mobile-nav')) return;
      each('.mobile-navigation-drawer', document, (drawer) => {
        if (!drawer.querySelector(ownMenu)) restoreImages(drawer);
      });
    };
    observe(document.body, { attributes: true, attributeFilter: ['class'] }, sync);
    sync();
  };
  let cardVideoEventsReady = false;
  const initCardHoverVideo = () => {
    if (cardVideoEventsReady || document.documentElement.dataset.eykCardVideoReady) return;
    const gate = window.matchMedia('(any-hover: hover)');
    const touchGate = window.matchMedia('(hover: none) and (pointer: coarse)');
    const connection = navigator.connection;
    const selector = 'product-block[data-eyk-card]';
    const controls = '.quickbuy-container, .quickbuy-toggle:not(.product-link), .product-block__quick-add, .product-block-options, .product-sibling-swatches, button, input, select, textarea';
    const hydrated = new WeakSet();
    const failed = new WeakSet();
    const cards = new Map();
    const unloaded = new WeakSet();
    const pointers = new Set();
    // Retain the part-3 scheduler: 60% enter, 30% leave, 300 ms settled winner.
    const startRatio = 0.6;
    const stopRatio = 0.3;
    const settleMs = 300;
    let active = null;
    let press = null;
    let suppressed = null;
    let tapGesture = null;
    let lastPointerType = null;
    let pending = null;
    let candidate = null;
    let inviewObserver = null;
    let touchSuspended = false;
    let lastScrollAt = -Infinity;
    const mode = (card) => ['off', 'tap', 'press', 'inview', 'both'].includes(card.dataset.eykTouchPreview)
      ? card.dataset.eykTouchPreview : 'tap';
    const pressMode = (card) => ['press', 'both'].includes(mode(card));
    const inviewMode = (card) => ['inview', 'both'].includes(mode(card));
    // Static media permission is deliberately separate from video permission.
    const touchAllowed = () => !connection?.saveData && !['slow-2g', '2g'].includes(connection?.effectiveType);
    const cardOf = (node) => node instanceof Element ? node.closest(selector) : null;
    const surfaceOf = (node) => node instanceof Element && !node.closest(controls)
      ? node.closest('[data-eyk-card-media]') : null;
    const inView = (card) => {
      const rect = card.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.right > 0
        && rect.top < window.innerHeight && rect.left < window.innerWidth;
    };
    const allowed = (card, route) => !document.hidden && !touchSuspended && card.isConnected
      && !unloaded.has(card) && inView(card) && (route === 'hover' ? gate.matches
        : touchGate.matches && (route === 'tap' ? mode(card) === 'tap' : route === 'press' ? pressMode(card)
          : inviewMode(card) && cards.has(card) && !cards.get(card).manual
            && cards.get(card).ratio > stopRatio));
    const cancelPending = () => {
      if (pending !== null) window.clearTimeout(pending);
      pending = null;
      candidate = null;
    };
    const reset = (video) => {
      if (!video) return;
      video.loop = false;
      video.classList.remove('eyk-card-video--playing');
      safe(() => video.pause());
      safe(() => { video.currentTime = 0; });
    };
    const stop = () => {
      const state = active;
      active = null; // Invalidates every outstanding play promise before pause().
      if (!state) return;
      state.card.classList.remove('eyk-card-preview', 'eyk-card-on-video', 'eyk-card-secondary-ready');
      reset(state.video);
    };
    // All interrupted presses, including pending timers, leave by this path.
    const cancel = () => {
      if (press?.timer !== null && press?.timer !== undefined) window.clearTimeout(press.timer);
      press = null;
      cancelPending();
      stop();
    };
    // A pointer starting or cancelling a gesture must not clear a latched tap.
    const cancelUnlessTap = () => { if (active?.route !== 'tap') cancel(); };
    const syncReady = (card, slot) => {
      if (active?.card === card && active.slot === slot) {
        card.classList.toggle('eyk-card-secondary-ready', slot.classList.contains('is-loaded'));
      }
    };
    const start = (card, route = 'hover') => {
      const slot = card.querySelector('.eyk-card-secondary');
      if (!slot || slot.dataset.mediaId !== card.dataset.eykHoverMedia || !allowed(card, route)) return false;
      if (active?.card === card && active.route === route) return true;
      stop();
      const video = slot.querySelector('video[data-eyk-card-video]');
      const state = {card, slot, video, route};
      active = state;
      card.classList.add('eyk-card-preview');
      card.classList.toggle('eyk-card-on-video', card.hasAttribute('data-eyk-second-video'));
      const img = slot.querySelector('img[data-eyk-image-src]');
      if (img && !img.hasAttribute('src')) {
        img.sizes = `${Math.ceil(card.getBoundingClientRect().width)}px`;
        if (img.dataset.eykImageSrcset) img.srcset = img.dataset.eykImageSrcset;
        img.src = img.dataset.eykImageSrc;
      }
      if (img?.complete && img.naturalWidth > 0) slot.classList.add('is-loaded');
      syncReady(card, slot);
      if (video && !video.hasAttribute('poster') && video.dataset.eykPoster) video.poster = video.dataset.eykPoster;
      if (!video || !touchAllowed() || failed.has(video)) return true;
      const fail = () => safe(() => {
        if (active !== state) return;
        failed.add(video);
        reset(video); // Keep the static secondary, including its contrast state.
      });
      try {
        const source = video.querySelector('source[data-eyk-video-src]');
        if (!source?.dataset.eykVideoSrc) return true;
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        video.loop = true;
        if (!hydrated.has(video)) {
          source.setAttribute('src', source.dataset.eykVideoSrc);
          hydrated.add(video);
          video.load();
        }
        const result = video.play();
        if (result?.then) result.then(() => safe(() => {
          if (active === state) {
            if (!allowed(card, route) || !touchAllowed() || !slot.isConnected || !video.isConnected) cancel();
            else if (!failed.has(video)) video.classList.add('eyk-card-video--playing');
          } else if (active?.video !== video) reset(video);
        })).catch(fail);
        else fail();
      } catch (error) { fail(); }
      return true;
    };
    const trackCards = (root, revive = false) => {
      const track = (card) => {
        if (revive) unloaded.delete(card);
        if (!card.isConnected || unloaded.has(card)) return;
        const slot = card.querySelector('.eyk-card-secondary');
        if (!slot || slot.dataset.mediaId !== card.dataset.eykHoverMedia) return;
        if (cards.get(card)?.slot === slot) return;
        if (cards.has(card)) inviewObserver?.unobserve(card);
        cards.set(card, {card, slot, ratio: 0, manual: false});
        inviewObserver?.observe(card);
      };
      const parent = cardOf(root);
      if (parent) track(parent);
      root.querySelectorAll?.(selector).forEach(track);
    };
    const pruneCards = () => {
      cards.forEach((entry, card) => {
        if (!card.isConnected || !entry.slot.isConnected) {
          if (press?.card === card || active?.card === card || candidate?.card === card) cancel();
          if (suppressed?.card === card) suppressed = null;
          inviewObserver?.unobserve(card);
          cards.delete(card);
        }
      });
    };
    const readIntersections = (entries) => {
      entries.forEach((entry) => {
        const item = cards.get(entry.target);
        if (!item) return;
        item.ratio = entry.isIntersecting ? entry.intersectionRatio : 0;
        if (item.ratio === 0) {
          if (active?.card === item.card || press?.card === item.card) cancel();
          item.manual = false; // Only a complete exit clears press ownership.
        }
      });
    };
    const chooseCard = () => {
      if (!touchGate.matches || touchSuspended || document.hidden || press || active?.route === 'hover' || active?.route === 'tap') return null;
      let winner = active?.route === 'inview' && allowed(active.card, 'inview') ? cards.get(active.card) : null;
      cards.forEach((entry) => {
        if (entry.ratio < startRatio || !allowed(entry.card, 'inview')) return;
        if (!winner || entry.ratio > winner.ratio) winner = entry;
      });
      return winner;
    };
    const settleTouch = (moved = false) => {
      const winner = chooseCard();
      if (active?.route === 'inview' && active.card !== winner?.card) stop();
      if (!winner || active?.card === winner.card) { cancelPending(); return; }
      if (!moved && candidate === winner) return;
      cancelPending();
      candidate = winner;
      pending = window.setTimeout(() => safe(() => {
        pending = null;
        candidate = null;
        if (inviewObserver) readIntersections(inviewObserver.takeRecords());
        const latest = chooseCard();
        if (latest === winner) start(winner.card, 'inview');
        else settleTouch();
      }), settleMs);
    };
    if ('IntersectionObserver' in window) {
      inviewObserver = new IntersectionObserver((entries) => safe(() => {
        readIntersections(entries);
        settleTouch(true);
      }), {threshold: Array.from({length: 101}, (_, index) => index / 100)});
    }
    trackCards(document);
    const changes = new MutationObserver((records) => safe(() => {
      if (records.some((r) => r.type === 'childList')) {
        pruneCards();
        records.forEach((r) => {
          if (r.type !== 'childList') return;
          trackCards(r.target);
          r.addedNodes.forEach((node) => { if (node instanceof Element) trackCards(node); });
        });
      }
      if (records.some((r) => r.type === 'attributes' && r.attributeName.startsWith('data-eyk-'))) cancel();
      // Quickbuy/cart drawer opening, or a section becoming hidden, cancels too.
      if (document.querySelector('product-block.expanded')
          || document.body.classList.contains('cart-drawer-open')) cancel();
      if (press && (!press.card.isConnected || !press.surface.isConnected || !allowed(press.card, 'press'))) cancel();
      if (active && (!allowed(active.card, active.route) || !active.slot.isConnected)) cancel();
      settleTouch();
    }));
    changes.observe(document.body, {childList: true, subtree: true, attributes: true,
      attributeFilter: ['data-eyk-touch-preview', 'data-eyk-hover-media', 'data-eyk-video-contrast', 'class']});
    document.addEventListener('load', (event) => safe(() => {
      if (!(event.target instanceof Element) || !event.target.matches('img[data-eyk-image-src]')) return;
      const slot = event.target.closest('.eyk-card-secondary');
      const card = cardOf(slot);
      if (slot && card) { slot.classList.add('is-loaded'); syncReady(card, slot); }
    }), true);
    document.addEventListener('pointerover', (event) => safe(() => {
      if (!event.isTrusted || event.pointerType !== 'mouse' || event.buttons !== 0) return;
      if (event.target instanceof Element && event.target.closest(controls)) { cancel(); return; }
      const card = cardOf(event.target);
      if (!card || cardOf(event.relatedTarget) === card) return;
      start(card, 'hover');
    }), {passive: true});
    document.addEventListener('pointerout', (event) => safe(() => {
      if (active?.route === 'hover' && cardOf(event.relatedTarget) !== active.card) cancel();
      if (press && event.target instanceof Element && press.surface.contains(event.target)
          && !(event.relatedTarget instanceof Element && press.surface.contains(event.relatedTarget))) cancel();
    }), {passive: true});
    document.addEventListener('pointerdown', (event) => safe(() => {
      suppressed = null; // A new gesture can never inherit an old suppressed click.
      tapGesture = null;
      lastPointerType = event.pointerType;
      if (event.pointerType !== 'touch') { cancelUnlessTap(); return; }
      pointers.add(event.pointerId);
      if (!event.isPrimary || pointers.size !== 1) { cancelUnlessTap(); return; }
      const card = cardOf(event.target);
      const surface = surfaceOf(event.target);
      const control = event.target instanceof Element && !!event.target.closest(controls);
      // Only a completed background tap releases a preview; scrolling never does.
      if (!card && !control) tapGesture = {pointerId: event.pointerId, x: event.clientX, y: event.clientY};
      if (card && !control && active?.route === 'tap' && active.card !== card) cancel();
      if (card && mode(card) === 'tap') {
        // Start in this touch event, including during momentum scrolling.
        if (!control && !(active?.card === card && active.route === 'tap')) {
          cancel();
          start(card, 'tap');
        }
        return;
      }
      cancelUnlessTap();
      if (active?.route === 'tap') return;
      if (!card || !surface || !allowed(card, 'press') || Date.now() - lastScrollAt < 150) return;
      const gesture = {card, surface, pointerId: event.pointerId, x: event.clientX, y: event.clientY, fired: false, timer: null};
      press = gesture;
      gesture.timer = window.setTimeout(() => safe(() => {
        gesture.timer = null;
        if (press !== gesture || pointers.size !== 1 || !surface.isConnected || !allowed(card, 'press')
            || Date.now() - lastScrollAt < 150) { cancel(); return; }
        if (cards.has(card)) cards.get(card).manual = true;
        gesture.fired = start(card, 'press');
        if (gesture.fired) suppressed = {card, surface, pointerId: gesture.pointerId};
      }), 300);
    }), {passive: true});
    document.addEventListener('pointermove', (event) => safe(() => {
      if (tapGesture?.pointerId === event.pointerId
          && Math.hypot(event.clientX - tapGesture.x, event.clientY - tapGesture.y) > 10) tapGesture = null;
      if (press?.pointerId === event.pointerId && Math.hypot(event.clientX - press.x, event.clientY - press.y) > 10) cancel();
    }), {passive: true});
    document.addEventListener('pointerup', (event) => safe(() => {
      pointers.delete(event.pointerId);
      if (press?.pointerId === event.pointerId) cancel();
      if (tapGesture?.pointerId !== event.pointerId) return;
      const gesture = tapGesture;
      tapGesture = null;
      if (event.pointerType !== 'touch' || !event.isPrimary || pointers.size !== 0
          || Math.hypot(event.clientX - gesture.x, event.clientY - gesture.y) > 10
          || cardOf(event.target)
          || (event.target instanceof Element && event.target.closest(controls))) return;
      // Background taps may have no click in Safari; completed pointerup is enough.
      if (active?.route === 'tap') cancel();
    }), {passive: true});
    document.addEventListener('pointercancel', (event) => safe(() => {
      pointers.delete(event.pointerId);
      suppressed = null;
      tapGesture = null;
      cancelUnlessTap();
    }), {passive: true});
    document.addEventListener('contextmenu', (event) => {
      if (press && pressMode(press.card) && touchGate.matches && cardOf(event.target) === press.card && surfaceOf(event.target)) event.preventDefault();
      const card = cardOf(event.target);
      if (card && mode(card) === 'tap' && touchGate.matches
          && (event.pointerType || lastPointerType) === 'touch'
          && !event.target.closest(controls)) event.preventDefault();
    });
    document.addEventListener('click', (event) => safe(() => {
      const card = cardOf(event.target);
      const associated = suppressed && card === suppressed.card && surfaceOf(event.target) === suppressed.surface
        && event.pointerId === suppressed.pointerId && event.detail !== 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
      if (associated) {
        suppressed = null;
        cancel();
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      cancelUnlessTap();
      const swatch = event.target instanceof Element ? event.target.closest('.product-block-options__item[data-eyk-variant-id]') : null;
      if (!card || !swatch) return;
      event.preventDefault();
      card.querySelectorAll('.product-link, .quickbuy-toggle').forEach((link) => {
        const url = new URL(link.href, document.baseURI);
        url.searchParams.set('variant', swatch.dataset.eykVariantId);
        link.href = `${url.pathname}${url.search}${url.hash}`;
        link.rel = 'nofollow';
      });
    }), true);
    document.addEventListener('error', (event) => safe(() => {
      const video = active?.video;
      if (video && (event.target === video || video.contains(event.target))) { failed.add(video); reset(video); }
    }), true);
    document.addEventListener('scroll', () => safe(() => {
      lastScrollAt = Date.now();
      if (press) cancel();
      if (active && !allowed(active.card, active.route)) cancel();
      settleTouch(true);
    }), {passive: true, capture: true});
    window.addEventListener('resize', () => safe(() => {
      if (press || (active && !allowed(active.card, active.route))) cancel();
      settleTouch(true);
    }), {passive: true});
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape') cancel(); });
    const suspend = () => {
      touchSuspended = true;
      pointers.clear();
      suppressed = null;
      tapGesture = null;
      lastPointerType = null;
      cancel();
    };
    const resume = () => { touchSuspended = false; settleTouch(true); };
    document.addEventListener('visibilitychange', () => { if (document.hidden) suspend(); else resume(); });
    window.addEventListener('blur', suspend);
    window.addEventListener('pagehide', suspend);
    window.addEventListener('focus', resume);
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        pointers.clear();
        suppressed = null;
        tapGesture = null;
        lastPointerType = null;
        if (active?.route === 'tap') cancel();
      }
      resume();
    });
    const gateChanged = () => { cancel(); settleTouch(true); };
    [gate, touchGate].forEach((query) => {
      if (query.addEventListener) query.addEventListener('change', gateChanged);
      else query.addListener(gateChanged);
    });
    const videoPermissionChanged = () => {
      const previous = active;
      const gesture = press;
      cancel();
      // Preserve the static preview when a running video loses permission.
      if (previous && !touchAllowed()) {
        if (previous.route === 'press' && gesture?.fired && pointers.has(gesture.pointerId)) {
          press = gesture;
          start(previous.card, 'press');
        } else if (previous.route !== 'press') start(previous.card, previous.route);
      }
      settleTouch(true);
    };
    connection?.addEventListener?.('change', videoPermissionChanged);
    document.addEventListener('shopify:section:unload', (event) => safe(() => {
      if (!(event.target instanceof Element)) return;
      if ((press && event.target.contains(press.card)) || (active && event.target.contains(active.card))) cancel();
      cards.forEach((entry, card) => {
        if (!event.target.contains(card)) return;
        unloaded.add(card);
        inviewObserver?.unobserve(card);
        cards.delete(card);
      });
      settleTouch();
    }));
    document.addEventListener('shopify:section:load', (event) => safe(() => { trackCards(event.target, true); settleTouch(); }));
    cardVideoEventsReady = true;
    document.documentElement.dataset.eykCardVideoReady = 'true';
  };

  const init = () => safe(() => {
    observers.forEach((observer) => observer.disconnect());
    observers = [];
    // The old menu has neither hook: no nodes, hrefs, body classes or events change.
    if (!document.querySelector(`.mobile-navigation-drawer ${ownMenu}, main-navigation ${promo}, .mobile-navigation-drawer ${promo}`)) {
      if (document.body.classList.contains('eyk-nav-ready') || document.body.classList.contains('eyk-nav-open')) {
        document.body.classList.remove('eyk-nav-ready', 'eyk-nav-open');
      }
      drawerWasOpen = false;
      openPanels.clear();
      return;
    }
    safe(prepareImages); // Before PageHeader's timer or the drawer's manual loader.
    safe(() => each('.mobile-navigation-drawer', document, prepareDrawer));
    safe(prepareStockDrawerImages);
    safe(prepareScrim);
    safe(prepareEvents);
    safe(buildSearchTiles);
  });

  init(); // main.js is the preceding deferred classic script; its drawer exists now.
  safe(initCardHoverVideo);
  document.addEventListener('shopify:section:load', () => {
    init();
    // Also cover editor handlers that rebuild the header later in the same event.
    safe(() => window.requestAnimationFrame(init));
  });
})();

/* EYEKØNIK product-column videos: native controls in the original tile. */
(() => {
  if (window.eykInlineProductVideosReady) return;
  window.eykInlineProductVideosReady = true;
  const selector = '.product-column-right [class*="video-slide-video-"]';
  const prepared = new WeakSet();
  const columns = new WeakSet();
  let listening = false;
  let active = null;
  let gesture = null;
  const tileOf = (node) => node instanceof Element ? node.closest(selector) : null;
  const idleButton = (tile) => {
    tile.setAttribute('role', 'button');
    tile.setAttribute('tabindex', '0');
    tile.setAttribute('aria-label', 'Play video');
  };
  const quietPlay = (video) => {
    try { video.play()?.catch(() => {}); } catch (_) { /* Keep the poster available. */ }
  };
  const reset = (tile) => {
    const video = tile.querySelector('video');
    if (!video) return;
    if (active?.tile === tile) active = null;
    video.controls = false;
    video.muted = true;
    video.loop = true;
    tile.classList.remove('eyk-video-playing');
    idleButton(tile);
    quietPlay(video);
  };
  const start = (tile, keyboard = false) => {
    const video = tile.querySelector('video');
    if (!video || tile.classList.contains('eyk-video-playing')) return;
    if (active) reset(active.tile);
    const state = { tile, video };
    active = state;
    video.controls = true;
    video.muted = false;
    video.loop = false;
    video.currentTime = 0;
    tile.classList.add('eyk-video-playing');
    tile.removeAttribute('role');
    tile.removeAttribute('tabindex');
    tile.removeAttribute('aria-label');
    const failed = () => { if (active === state) reset(tile); };
    // Synchronous with the user gesture so iOS permits audible playback.
    try { video.play()?.catch(failed); } catch (_) { failed(); }
    if (keyboard && active === state) video.focus();
  };
  const bindEvents = () => {
    if (listening) return;
    listening = true;
    document.addEventListener('pointerdown', (event) => {
      const tile = tileOf(event.target);
      gesture = tile ? { tile, id: event.pointerId, x: event.clientX, y: event.clientY, dragged: false } : null;
    }, { capture: true, passive: true });
    const measure = (event) => {
      if (gesture?.id === event.pointerId && Math.hypot(event.clientX - gesture.x, event.clientY - gesture.y) > 6) {
        gesture.dragged = true;
      }
    };
    document.addEventListener('pointermove', measure, { capture: true, passive: true });
    document.addEventListener('pointerup', measure, { capture: true, passive: true });
    document.addEventListener('pointercancel', () => { if (gesture) gesture.dragged = true; }, { capture: true, passive: true });
    document.addEventListener('click', (event) => {
      const tile = tileOf(event.target);
      if (!tile || !tile.querySelector('video')) return;
      const dragged = gesture?.dragged && gesture.tile.closest('.swiper') === tile.closest('.swiper');
      gesture = null;
      if (dragged || tile.closest('.swiper')?.swiper?.allowClick === false) return;
      const video = tile.querySelector('video');
      // Native controls own subsequent clicks; the app only opens on the tile id.
      if (tile.classList.contains('eyk-video-playing') && event.target === video && video.controls) return;
      event.stopImmediatePropagation();
      event.preventDefault();
      start(tile);
    }, true);
    document.addEventListener('keydown', (event) => {
      const tile = tileOf(event.target);
      if (!tile || event.target !== tile || !tile.querySelector('video')
          || tile.classList.contains('eyk-video-playing') || !['Enter', ' '].includes(event.key)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (!event.repeat) start(tile, true);
    }, true);
  };
  const prepare = (column) => {
    column.querySelectorAll('[class*="video-slide-video-"]').forEach((tile) => {
      const video = tile.querySelector('video');
      if (!video || prepared.has(video)) return;
      prepared.add(video);
      idleButton(tile);
      video.addEventListener('ended', () => {
        if (tile.classList.contains('eyk-video-playing')) reset(tile);
      });
      bindEvents();
    });
    if (active && !active.tile.isConnected) reset(active.tile);
  };
  const init = () => {
    document.querySelectorAll('.product-column-right').forEach((column) => {
      if (columns.has(column)) return;
      columns.add(column);
      prepare(column);
      // Section Store moves its template into the column after DOMContentLoaded.
      new MutationObserver(() => prepare(column)).observe(column, { childList: true, subtree: true });
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  document.addEventListener('shopify:section:load', init);
})();

/* EYEKONIK RUN J — BEGIN: seasonal trends and mobile native-scroll loop. */
(() => {
  'use strict';
  if (window.eykTrendsRunJ) {
    window.eykTrendsRunJ();
    return;
  }
  const sectionSelector = '.shopify-section[id$="__eyk_trends"]';
  const cloneAttribute = 'data-eyk-trends-clone';
  const mobile = window.matchMedia('(max-width: 767.98px)');
  const states = new Map();
  const title = (tile) => (tile.querySelector('.text-overlay__subheading')?.textContent || '').trim().toLowerCase();
  const realTiles = (grid) => Array.from(grid.children).filter((tile) =>
    tile.matches('.gallery__item') && !tile.hasAttribute(cloneAttribute));

  const startTitle = () => {
    const productStart = document.querySelector('meta[name="eyk-trends-start"]')?.content;
    if (productStart === 'Active' || productStart === 'Winter') return productStart.toLowerCase();
    const shopify = window.Shopify;
    const preview = shopify?.designMode || (shopify?.theme?.role && shopify.theme.role !== 'main');
    const override = preview ? new URLSearchParams(window.location.search).get('eyk_season_date') : null;
    let monthDay;
    // Reject impossible dates as well as malformed input; never parse a bare date in local time.
    if (override && /^\d{4}-\d{2}-\d{2}$/.test(override)) {
      const date = new Date(override + 'T12:00:00Z');
      if (Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === override) {
        monthDay = Number(override.slice(5, 7)) * 100 + Number(override.slice(8, 10));
      }
    }
    if (monthDay === undefined) {
      const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/Amsterdam', month: '2-digit', day: '2-digit'
      }).formatToParts(new Date());
      monthDay = Number(parts.find((part) => part.type === 'month').value) * 100
        + Number(parts.find((part) => part.type === 'day').value);
    }
    if (monthDay >= 1221 || monthDay < 320) return 'winter';
    if (monthDay < 621) return 'spring';
    if (monthDay < 923) return 'summer';
    return 'fall';
  };

  const makeClone = (tile, index) => {
    const clone = tile.cloneNode(true);
    clone.setAttribute(cloneAttribute, String(index));
    clone.setAttribute('aria-hidden', 'true');
    // tabindex, rather than inert, preserves native taps on the unchanged collection links.
    [clone, ...clone.querySelectorAll('*')].forEach((node) => {
      node.removeAttribute('id');
      node.removeAttribute('autofocus');
      ['aria-labelledby', 'aria-describedby', 'aria-controls', 'for'].forEach((name) => node.removeAttribute(name));
      if (node.matches('a, area, button, input, select, textarea, summary, iframe, [tabindex], [contenteditable]')) {
        node.setAttribute('tabindex', '-1');
      }
      if (node.hasAttribute('contenteditable')) node.setAttribute('contenteditable', 'false');
    });
    return clone;
  };

  const bind = (grid, tiles) => {
    const slider = grid.closest('.slider.slider--no-scrollbar');
    if (!slider) return null;
    const abort = new AbortController();
    const options = { passive: true, signal: abort.signal };
    const supportsScrollEnd = 'onscrollend' in slider;
    const pointers = new Set();
    const imageLoading = new Map();
    let touches = 0;
    let looped = false;
    let timer = 0;
    let frame = 0;
    let pendingEnd = false;
    let pendingSync = false;
    let inset = 0;
    const held = () => touches > 0 || pointers.size > 0;
    const refreshCarousel = () => slider.closest('carousel-slider')?.refresh?.();
    const contentX = (tile) => tile.getBoundingClientRect().left - slider.getBoundingClientRect().left + slider.scrollLeft;
    const restoreSnap = () => {
      cancelAnimationFrame(frame);
      slider.classList.remove('eyk-trends-jump');
    };
    const jump = (left) => {
      cancelAnimationFrame(frame);
      slider.classList.add('eyk-trends-jump');
      // Commit snap/behaviour overrides before assigning scrollLeft (stock uses smooth).
      void slider.offsetWidth;
      slider.scrollLeft = left;
      void slider.offsetWidth;
      frame = requestAnimationFrame(() => slider.classList.remove('eyk-trends-jump'));
    };
    const settle = () => {
      clearTimeout(timer);
      if (!looped || !mobile.matches || !slider.isConnected) return;
      if (held()) { pendingEnd = true; return; }
      pendingEnd = false;
      // Use the actual matching nodes, not rounded widths or scrollWidth / 3.
      // This preserves the residual pixel offset, including fractional scroll positions.
      const edge = slider.getBoundingClientRect().left + inset;
      let leading = grid.firstElementChild;
      for (const tile of grid.children) {
        if (tile.getBoundingClientRect().left > edge + 0.01) break;
        leading = tile;
      }
      if (!leading?.hasAttribute(cloneAttribute)) return;
      const original = tiles[Number(leading.getAttribute(cloneAttribute))];
      if (!original) return;
      const delta = original.getBoundingClientRect().left - leading.getBoundingClientRect().left;
      jump(slider.scrollLeft + delta);
    };
    const quiet = () => {
      clearTimeout(timer);
      timer = setTimeout(settle, 120);
    };
    const removeClones = () => {
      clearTimeout(timer);
      grid.querySelectorAll('[' + cloneAttribute + ']').forEach((clone) => clone.remove());
      imageLoading.forEach((loading, img) => {
        if (loading === null) img.removeAttribute('loading');
        else img.setAttribute('loading', loading);
      });
      imageLoading.clear();
      slider.classList.remove('eyk-trends-loop');
      looped = false;
    };
    const sync = () => {
      if (!mobile.matches) {
        pendingSync = false;
        if (looped) {
          removeClones();
          restoreSnap();
          // The desktop grid has no horizontal overflow and clamps naturally during a touch.
          if (!held()) jump(0);
          refreshCarousel();
        }
        return;
      }
      if (held()) { pendingSync = true; return; }
      pendingSync = false;
      if (looped || tiles.length < 2 || !slider.clientWidth) return;
      // Remember the stock leading inset before inserting the prefix cycle.
      inset = contentX(tiles[0]);
      const before = document.createDocumentFragment();
      const after = document.createDocumentFragment();
      tiles.forEach((tile, index) => {
        // Warm both copies of each image so a lazy-load placeholder cannot flash on rebasing.
        tile.querySelectorAll('img').forEach((img) => {
          imageLoading.set(img, img.getAttribute('loading'));
          img.setAttribute('loading', 'eager');
        });
        before.appendChild(makeClone(tile, index));
        after.appendChild(makeClone(tile, index));
      });
      slider.classList.add('eyk-trends-loop', 'eyk-trends-jump');
      grid.prepend(before);
      grid.append(after);
      looped = true;
      jump(contentX(tiles[0]) - inset);
      refreshCarousel();
    };
    const release = () => {
      if (held()) return;
      if (pendingSync) sync();
      // Also cover scrollend received while a finger was still resting on the glass.
      if (pendingEnd || !supportsScrollEnd) quiet();
    };
    slider.addEventListener('scroll', () => {
      if (!supportsScrollEnd || pendingEnd) quiet();
    }, options);
    if (supportsScrollEnd) slider.addEventListener('scrollend', settle, options);
    slider.addEventListener('touchstart', (event) => {
      touches = event.touches.length;
      restoreSnap();
      clearTimeout(timer);
    }, options);
    const touchRelease = (event) => { touches = event.touches.length; release(); };
    document.addEventListener('touchend', touchRelease, options);
    document.addEventListener('touchcancel', touchRelease, options);
    slider.addEventListener('pointerdown', (event) => {
      pointers.add(event.pointerId);
      restoreSnap();
      clearTimeout(timer);
    }, options);
    const pointerRelease = (event) => { pointers.delete(event.pointerId); release(); };
    document.addEventListener('pointerup', pointerRelease, options);
    document.addEventListener('pointercancel', pointerRelease, options);
    // A touchstart remains authoritative after the browser cancels its pointer for native scrolling.
    const resize = new ResizeObserver(() => sync());
    resize.observe(slider);
    sync();
    // Symmetry may have cached the original node order before its lazy initialization.
    if (!looped) refreshCarousel();
    return {
      tiles, sync,
      destroy() {
        abort.abort();
        resize.disconnect();
        restoreSnap();
        removeClones();
      }
    };
  };

  const refresh = () => {
    states.forEach((state, grid) => {
      if (!grid.isConnected) { state.destroy(); states.delete(grid); }
    });
    document.querySelectorAll(sectionSelector + ' .slider__grid').forEach((grid) => {
      let tiles = realTiles(grid);
      const state = states.get(grid);
      const start = tiles.findIndex((tile) => title(tile) === startTitle());
      const sameTiles = state && tiles.length === state.tiles.length && tiles.every((tile, index) => tile === state.tiles[index]);
      if (sameTiles && start <= 0) { state.sync(); return; }
      if (state) { state.destroy(); states.delete(grid); }
      if (start > 0) {
        tiles = tiles.slice(start).concat(tiles.slice(0, start));
        tiles.forEach((tile) => grid.appendChild(tile));
      }
      const next = bind(grid, tiles);
      if (next) states.set(grid, next);
    });
  };
  window.eykTrendsRunJ = refresh;
  mobile.addEventListener('change', () => states.forEach((state) => state.sync()));
  document.addEventListener('shopify:section:load', refresh);
  document.addEventListener('shopify:section:unload', (event) => {
    states.forEach((state, grid) => {
      if (event.target.contains(grid)) { state.destroy(); states.delete(grid); }
    });
  });
  const init = () => {
    refresh();
    // Section Rendering API replacements can also arrive without a Shopify editor event.
    new MutationObserver((records) => {
      const relevant = records.some((record) =>
        (record.target.matches?.('.slider__grid') && record.target.closest(sectionSelector))
        || [...record.addedNodes, ...record.removedNodes].some((node) => node.nodeType === 1
          && (node.matches(sectionSelector + ', ' + sectionSelector + ' .slider__grid')
            || node.querySelector(sectionSelector + ', ' + sectionSelector + ' .slider__grid'))));
      if (relevant) refresh();
    }).observe(document.body, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
/* EYEKONIK RUN J — END. */
